import { useEffect, useId, useState, type ChangeEvent } from 'react';
import { listOrgs, selectOrg, type OrgMembership } from '../../lib/api-client';
import { useAuth } from '../../features/onboarding/AuthContext';
import { Label } from '../../components/ui/Label';
import { Spinner } from '../../components/ui/Spinner';

/**
 * Org switcher (P3 — "alternar entre organizações com o mesmo login",
 * TTP-244). Mirrors `CnpjStep.tsx`'s exact select-org sequence: `POST
 * /internal/orgs/{orgId}/select` (sets the `orgId` custom claim
 * server-side) then `refreshOrgClaim()` (forces `getIdToken(true)` so the
 * NEW claim is visible client-side). Every downstream context/hook keyed on
 * `orgId`/the session API key (`DashboardSessionKeyContext.tsx`,
 * `useLabels.ts`, etc.) re-fetches automatically once `orgId` changes — no
 * separate cache-invalidation call needed here.
 */
export function OrgSwitcher() {
  const { user, orgId, refreshOrgClaim } = useAuth();
  const selectId = useId();
  const [orgs, setOrgs] = useState<OrgMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    user
      .getIdToken()
      .then((idToken) => listOrgs(idToken))
      .then((list) => {
        if (!cancelled) setOrgs(list);
      })
      .catch(() => {
        if (!cancelled) setError('Não foi possível carregar suas organizações.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Re-list on every `orgId` change too — a switch elsewhere (or a
    // membership change) should keep this list fresh, not just the
    // selected value.
  }, [user, orgId]);

  async function handleChange(event: ChangeEvent<HTMLSelectElement>) {
    const nextOrgId = event.target.value;
    if (!user || !nextOrgId || nextOrgId === orgId) {
      return;
    }
    setSwitching(true);
    setError(null);
    try {
      const idToken = await user.getIdToken();
      await selectOrg(idToken, nextOrgId);
      await refreshOrgClaim();
    } catch {
      setError('Não foi possível trocar de organização. Tente novamente.');
    } finally {
      setSwitching(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Spinner className="h-4 w-4" />
        <span className="font-mono text-xs uppercase tracking-widest">Organizações…</span>
      </div>
    );
  }

  if (orgs.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={selectId} className="sr-only">
        Organização
      </Label>
      <select
        id={selectId}
        value={orgId ?? ''}
        onChange={handleChange}
        disabled={switching}
        className="rounded-none border border-input bg-background px-3 py-2 font-mono text-xs uppercase tracking-widest text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {orgs.map((org) => (
          <option key={org.id} value={org.id}>
            {org.trade_name || org.legal_name}
          </option>
        ))}
      </select>
      {error ? (
        <p role="alert" className="text-xs font-medium text-accent">
          {error}
        </p>
      ) : null}
    </div>
  );
}
